import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Grid,
  Typography,
  MenuItem,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { format, differenceInDays, parseISO } from 'date-fns';

const statusOptions = ['Pending', 'Confirmed', 'Checked In', 'Checked Out', 'Cancelled'];
const paymentStatusOptions = ['pending', 'partial', 'paid'];
const paymentMethodOptions = [
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'debit_card', label: 'Debit Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
];

export default function EditBookingDialog({ open, onClose, booking, onSave }) {
  const [formData, setFormData] = useState({
    guest: '',
    email: '',
    phone: '',
    room: '',
    checkIn: '',
    checkOut: '',
    numberOfGuests: 1,
    specialRequests: '',
    status: 'Confirmed',
    paymentMethod: 'credit_card',
    paymentStatus: 'pending',
    depositAmount: 0,
  });
  
  const [errors, setErrors] = useState({});
  const [nights, setNights] = useState(1);

  useEffect(() => {
    if (booking) {
      setFormData({
        guest: booking.guest || '',
        email: booking.email || '',
        phone: booking.phone || '',
        room: booking.room || '',
        checkIn: booking.checkIn || '',
        checkOut: booking.checkOut || '',
        numberOfGuests: booking.numberOfGuests || 1,
        specialRequests: booking.specialRequests || '',
        status: booking.status || 'Confirmed',
        paymentMethod: booking.paymentMethod || 'credit_card',
        paymentStatus: booking.paymentStatus || 'pending',
        depositAmount: booking.depositAmount || 0,
      });
    }
  }, [booking]);

  useEffect(() => {
    if (formData.checkIn && formData.checkOut) {
      const checkIn = parseISO(formData.checkIn);
      const checkOut = parseISO(formData.checkOut);
      const nightsCount = differenceInDays(checkOut, checkIn);
      setNights(nightsCount > 0 ? nightsCount : 1);
    }
  }, [formData.checkIn, formData.checkOut]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.guest.trim()) newErrors.guest = 'Guest name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.checkIn) newErrors.checkIn = 'Check-in date is required';
    if (!formData.checkOut) newErrors.checkOut = 'Check-out date is required';
    
    if (formData.checkIn && formData.checkOut && parseISO(formData.checkOut) <= parseISO(formData.checkIn)) {
      newErrors.checkOut = 'Check-out must be after check-in';
    }
    
    if (!formData.numberOfGuests || formData.numberOfGuests < 1) {
      newErrors.numberOfGuests = 'Number of guests is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const updatedBooking = {
      ...booking,
      ...formData,
      nights,
    };

    onSave(updatedBooking);
    onClose();
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Edit Booking</Typography>
            <Typography variant="caption" color="text.secondary">
              Booking ID: {booking.id}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Guest Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Guest Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Guest Name"
              value={formData.guest}
              onChange={(e) => handleChange('guest', e.target.value)}
              error={!!errors.guest}
              helperText={errors.guest}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Room"
              value={formData.room}
              onChange={(e) => handleChange('room', e.target.value)}
              helperText="e.g., Deluxe 101"
            />
          </Grid>

          {/* Stay Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
              Stay Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Check-in Date"
              type="date"
              value={formData.checkIn}
              onChange={(e) => handleChange('checkIn', e.target.value)}
              error={!!errors.checkIn}
              helperText={errors.checkIn}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Check-out Date"
              type="date"
              value={formData.checkOut}
              onChange={(e) => handleChange('checkOut', e.target.value)}
              error={!!errors.checkOut}
              helperText={errors.checkOut}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Number of Guests"
              type="number"
              value={formData.numberOfGuests}
              onChange={(e) => handleChange('numberOfGuests', parseInt(e.target.value) || 1)}
              error={!!errors.numberOfGuests}
              helperText={errors.numberOfGuests}
              inputProps={{ min: 1 }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Number of Nights"
              value={nights}
              InputProps={{ readOnly: true }}
              disabled
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Special Requests"
              multiline
              rows={3}
              value={formData.specialRequests}
              onChange={(e) => handleChange('specialRequests', e.target.value)}
            />
          </Grid>

          {/* Booking Status */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
              Booking Status
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Booking Status"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Payment Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
              Payment Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Payment Method"
              value={formData.paymentMethod}
              onChange={(e) => handleChange('paymentMethod', e.target.value)}
            >
              {paymentMethodOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Payment Status"
              value={formData.paymentStatus}
              onChange={(e) => handleChange('paymentStatus', e.target.value)}
            >
              {paymentStatusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Deposit Amount"
              type="number"
              value={formData.depositAmount}
              onChange={(e) => handleChange('depositAmount', parseFloat(e.target.value) || 0)}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Alert severity="info">
              Changes will be saved immediately. Make sure all information is correct before saving.
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
